// src/lib/parseConflicts.js — what a second parse may change without asking.
//
// Owner: "When user parses text and the parses new one it shouldn't overwrite conflicting values
// silently. User must be asked do you want to replace this with this? Yes or no. If there are
// many ask on each one."
//
// And, from the same list, the constraint that makes it usable rather than a nag:
//   "Only call something a difference when there are two comparable known values that actually
//    conflict. Do not generate fake conflicts because one source did not provide a value."
//
// ⚠️ WHAT THE DESK DID BEFORE THIS. `handlePasteParse` applied every parsed field with
// `d.x || current` / `d.x ?? current`, so the newest parse won on every field it happened to
// mention — over the previous parse AND over anything the agent had typed by hand. Nothing was
// destroyed at the database (the claim model keeps every observation), but the FORM the agent
// was about to save silently changed under them, which is the same defect from where they sit.
//
// ⚠️ FOUR CASES, and only ONE of them is a question:
//   current empty, incoming has a value  → FILL     — apply silently, this is why they pasted
//   current has a value, incoming absent → KEEP     — silence, never "do you want to clear it?"
//   both present and EQUAL               → KEEP     — not a conflict; asking would train them to
//                                                     click through without reading
//   both present and DIFFERENT           → CONFLICT — ask, one question per field
// The middle two are what the owner's "do not generate fake conflicts" rules out, and they are
// the majority of fields on any real paste.

// Comparison is by MEANING, not by bytes: the form holds strings (an <input> always does) while
// a parse returns numbers, so 3 and "3" are the same answer and must not ask. Whitespace and
// case are not answers either.
// ⚠️ Deliberately NOT numeric-tolerant beyond exactness — 3.0 vs 3 is the same number, but 50
// vs 50.5 is a real disagreement about an area and has to be asked.
function sameValue(a, b) {
  if (a == null || b == null) return a == null && b == null
  const as = String(a).trim(), bs = String(b).trim()
  if (as === bs) return true
  if (as.toLowerCase() === bs.toLowerCase()) return true
  const an = Number(as), bn = Number(bs)
  if (as !== '' && bs !== '' && Number.isFinite(an) && Number.isFinite(bn)) return an === bn
  return false
}

// A value the agent has actually answered. '' is what an untouched text input holds, so it is
// ABSENCE — treating it as an answer would make every empty field a conflict on the first paste.
const isPresent = v => v !== null && v !== undefined && String(v).trim() !== ''

/**
 * @param {Array<{key:string, label:string, current:*, incoming:*}>} fields
 * @returns {{ apply: Record<string,*>, conflicts: Array<{key,label,from,to}> }}
 *   `apply` — safe to write now, no question.
 *   `conflicts` — one entry per field that needs a yes/no.
 */
export function splitParse(fields) {
  const apply = {}
  const conflicts = []
  for (const f of fields || []) {
    if (!f || !f.key) continue
    const hasNew = isPresent(f.incoming)
    const hasOld = isPresent(f.current)
    if (!hasNew) continue                                   // the parse said nothing — keep
    if (!hasOld) { apply[f.key] = f.incoming; continue }     // filling a blank — why they pasted
    if (sameValue(f.current, f.incoming)) continue          // agreement is not a conflict
    conflicts.push({ key: f.key, label: f.label || f.key, from: f.current, to: f.incoming })
  }
  return { apply, conflicts }
}

/**
 * The answers, turned into a patch. Only the fields the person said yes to.
 * ⚠️ Unanswered means KEEP, not replace — a dialog dismissed without reading must never be the
 * same as approving every change in it.
 */
export function acceptedPatch(conflicts, answers) {
  const out = {}
  for (const c of conflicts || []) if (answers?.[c.key] === true) out[c.key] = c.to
  return out
}
